import { useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireRole, RequireSesion, useAuth } from './components/AuthProvider';
import { rolesQueVen, type Pantalla } from './domain/permissions';
import { PanelLayout, MovilLayout } from './components/Layout';
import Login from './pages/Login';
import Registro from './pages/Registro';
import CambiarClave from './pages/CambiarClave';
import Hoy from './pages/panel/Hoy';
import ResumenGerencial from './pages/panel/ResumenGerencial';
import Planificacion from './pages/panel/Planificacion';
import Trabajadores from './pages/panel/Trabajadores';
import Campanias from './pages/panel/Campanias';
import CampaniaDetalle from './pages/panel/CampaniaDetalle';
import ImportarProgramacion from './pages/panel/ImportarProgramacion';
import Pdv from './pages/panel/Pdv';
import Asignaciones from './pages/panel/Asignaciones';
import Aprobaciones from './pages/panel/Aprobaciones';
import Validacion from './pages/panel/Validacion';
import BandejaDeFormularios from './pages/panel/BandejaDeFormularios';
import LevantamientoBandeja from './pages/panel/LevantamientoBandeja';
import LevantamientoInformacion from './pages/panel/LevantamientoInformacion';
import Cierres from './pages/panel/Cierres';
import Ajustes from './pages/panel/Ajustes';
import Academia from './pages/panel/Academia';
import AcademiaCurso from './pages/panel/AcademiaCurso';
import Documentos from './pages/panel/Documentos';
import Autorizaciones from './pages/panel/Autorizaciones';
import MiTurno from './pages/movil/MiTurno';
import MiMes from './pages/movil/MiMes';
import Marcar from './pages/movil/Marcar';
import Historial from './pages/movil/Historial';
import Canjes from './pages/movil/Canjes';
import FichaPuesto from './pages/movil/FichaPuesto';
import CierreJornada from './pages/movil/CierreJornada';
import CierreDeTurnoForm from './pages/movil/CierreDeTurnoForm';
import MiRuta from './pages/movil/MiRuta';
import MiSolicitud from './pages/movil/MiSolicitud';
import VisitaPuesto from './pages/movil/VisitaPuesto';
import { PANEL_ROLES, esRutaDeCompras, homeRoute } from './lib/homeRoute';

// El componente `Placeholder` se eliminó en la Task 37: ya no queda ninguna ruta
// provisional (`/panel/autorizaciones` y `/movil/solicitud` eran las dos últimas)
// y `noUnusedLocals` lo rechazaba (TS6133).

/**
 * Guarda de ruta a partir de la matriz de permisos, no de una lista de roles escrita a mano.
 *
 * Esconder el enlace en el menú no alcanza: la URL se puede escribir, se puede tener guardada
 * en favoritos o llegar por un enlace viejo. Esto no es la seguridad —esa es la RLS— pero
 * evita que alguien caiga en una pantalla que va a rebotarle consulta por consulta sin
 * entender por qué.
 */
function soloQuienVe(pantalla: Pantalla, element: ReactNode) {
  return <RequireRole roles={rolesQueVen(pantalla)}>{element}</RequireRole>;
}

function RedirectHome() {
  const { user, roles, loading } = useAuth();
  if (loading) return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const destino = homeRoute(roles);
  if (esRutaDeCompras(destino)) return <RecargaCompleta a={destino} />;
  return <Navigate to={destino} replace />;
}

/** `/cotizaciones/` es otra entrada de Vite: un <Navigate> la mandaría al comodín y de ahí a `/`, en bucle. */
function RecargaCompleta({ a }: { a: string }) {
  useEffect(() => {
    window.location.assign(a);
  }, [a]);
  return <div className="p-8 text-center text-gray-500">Abriendo Cotizaciones Treid…</div>;
}

export const router = createBrowserRouter([
  { path: '/', element: <RedirectHome /> },
  { path: '/login', element: <Login /> },
  { path: '/registro', element: <Registro /> },
  {
    // Fuera de /panel y /movil, y con `RequireSesion` (solo pide sesión, no rol): es la
    // pantalla a la que `RequireRole` manda a quien todavía usa la clave que le entregaron,
    // así que tiene que ser alcanzable por cualquier autenticado y sin volver a rebotar.
    path: '/cambiar-clave',
    element: (
      <RequireSesion>
        <CambiarClave />
      </RequireSesion>
    ),
  },
  {
    path: '/panel',
    // La lista sale de `PANEL_ROLES` y no escrita a mano: dos listas de los roles que entran al
    // panel se desincronizan, y la que se olvida deja a alguien fuera de su propio sistema. Es
    // exactamente lo que pasó al agregar `datos` (0061) — la matriz ya lo contemplaba y este
    // guarda no, así que el validador habría entrado y rebotado sin entender por qué.
    element: (
      <RequireRole roles={PANEL_ROLES}>
        <PanelLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <Navigate to="/panel/hoy" replace /> },
      { path: 'resumen', element: soloQuienVe('resumen', <ResumenGerencial />) },
      { path: 'hoy', element: soloQuienVe('hoy', <Hoy />) },
      // Los tres roles de oficina la necesitan, cada uno para lo suyo: el comercial para
      // comprobar lo que cargó, el coordinador y el supervisor para leer la programación
      // del mes que tienen que cubrir. Es el pedido explícito del usuario.
      { path: 'planificacion', element: soloQuienVe('planificacion', <Planificacion />) },
      { path: 'campanias', element: soloQuienVe('campanias', <Campanias />) },
      { path: 'campanias/:id', element: soloQuienVe('campanias', <CampaniaDetalle />) },
      // La carga mensual la hace comercial o el jefe de operaciones; el coordinador no.
      { path: 'importar-programacion', element: soloQuienVe('importar', <ImportarProgramacion />) },
      { path: 'asignaciones', element: soloQuienVe('asignaciones', <Asignaciones />) },
      { path: 'aprobaciones', element: soloQuienVe('aprobaciones', <Aprobaciones />) },
      { path: 'trabajadores', element: soloQuienVe('trabajadores', <Trabajadores />) },
      { path: 'documentos', element: soloQuienVe('documentos', <Documentos />) },
      { path: 'autorizaciones', element: soloQuienVe('autorizaciones', <Autorizaciones />) },
      { path: 'pdv', element: soloQuienVe('pdv', <Pdv />) },
      { path: 'validacion', element: soloQuienVe('validacion', <Validacion />) },
      // Bandeja de canjes: el tablero de Hoy solo mira el día en curso, así que los registros de
      // regularización quedaban invisibles. Acá se leen por rango y se corrigen con rastro.
      { path: 'canjes', element: soloQuienVe('canjes', <BandejaDeFormularios />) },
      // Bandeja del levantamiento: una fila por PUESTO medido dentro del mercado.
      { path: 'levantamiento', element: soloQuienVe('levantamiento', <LevantamientoBandeja />) },
      /**
       * Levantamiento de información: P&G (mercados y góndola) y, desde el 30-set, Yichang (canal
       * moderno, bodegas y mercados), con el cliente como primer control de la pantalla.
       *
       * La ruta sigue siendo `levantamiento-pg` y el permiso `levantamientoPg` aunque ya no sea solo
       * de P&G: es un cambio de rótulo, no de estructura (el mismo criterio de `/panel/canjes`), y
       * cambiar la URL rompería los enlaces guardados.
       *
       * ⚠️ La ruta de ARRIBA es otro estudio — el de SOFTYS, en pausa desde agosto — y los dos se
       * llaman «levantamiento». El 15-set un agente conectado a la base leyó el equivocado y
       * contestó con total seguridad.
       */
      { path: 'levantamiento-pg', element: soloQuienVe('levantamientoPg', <LevantamientoInformacion />) },
      // Academia: el catálogo de cursos y los resultados por recurso. El editor de un curso
      // cuelga de la misma pantalla porque comparte el permiso: quien ve el catálogo y puede
      // editar, edita; quien solo mira resultados, no llega al editor.
      { path: 'academia', element: soloQuienVe('academia', <Academia />) },
      { path: 'academia/curso/:cursoId', element: soloQuienVe('academia', <AcademiaCurso />) },
      { path: 'cierres', element: soloQuienVe('cierres', <Cierres />) },
      { path: 'ajustes', element: soloQuienVe('ajustes', <Ajustes />) },
    ],
  },
  {
    path: '/movil',
    // El supervisor (0110) entra acá y NO al panel: «Mi ruta» y los formularios de campo viven en
    // el celular. Sin esta línea el rol existiría en la base y en el padrón, y su titular quedaría
    // sin ninguna pantalla — el fallo silencioso que la BITACORA §20 anotó al crear el rol `datos`.
    element: (
      <RequireRole roles={['admin', 'comercial', 'coordinador', 'recurso', 'supervisor']}>
        <MovilLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <MiTurno /> },
      { path: 'marcar', element: <Marcar /> },
      { path: 'mi-mes', element: <MiMes /> },
      { path: 'historial', element: <Historial /> },
      // Canjes: la pantalla de trabajo de las campañas de dinámica. No se limita por tipo de
      // campaña en la ruta —eso lo decide la pantalla según el turno del día—, porque una misma
      // persona puede tener una campaña de impulso hoy y una de canjes mañana.
      { path: 'canjes', element: <Canjes /> },
      /**
       * Cierre de turno — la dinámica de IMPULSO (0090). Va al lado de canjes y no dentro, porque
       * son dos dinámicas distintas: en la de canje se registra la compra de un cliente; en esta
       * se reporta lo que la impulsadora puede contar al terminar, que es todo lo que se puede
       * medir cuando en la tienda no la dejan usar el celular.
       */
      { path: 'cierre-de-turno', element: <CierreDeTurnoForm /> },
      /**
       * Softys · Toallitas Húmedas: las visitas a puestos DENTRO de un mercado (0142-0144).
       *
       * Una sola pantalla para los dos instrumentos, y el tramo de la ruta decide cuál:
       * `comerciante` es la visita planificada —capacitación y pedido— y `venta` es la venta
       * cerrada con giro de ruleta. Se separan en la URL y no en un selector adentro porque el
       * pedido del jefe de operaciones fue tener DOS botones a la vista en «Mi turno», los dos
       * encendidos a la vez y alternables sin orden entre ellos.
       *
       * Sin guarda de rol propia: el padre ya exige uno de los cuatro, los botones solo aparecen
       * si la campaña tiene `visitas_puesto`, y quien decide de verdad es la base —la política de
       * escritura exige `worker_id = auth.uid()`.
       */
      { path: 'visita-puesto/:tipo', element: <VisitaPuesto /> },
      /**
       * La ruta del SUPERVISOR (0092). Vive en /movil y no en /panel por la misma razón que el
       * marcado: se llena de pie, en la puerta de la tienda. No lleva guarda de rol propio —el
       * padre ya exige uno de los cuatro— y quien no sea supervisor no ve la pestaña; el servidor
       * es el que decide de verdad: `registrar_visita_de_ruta` rechaza a quien no es coordinador.
       */
      { path: 'mi-ruta', element: <MiRuta /> },
      // Ficha de puesto del levantamiento de canal. Va en /movil y no en /panel porque se llena
      // DE PIE frente al puesto, en el mercado y sin señal — igual que el marcado y los canjes.
      { path: 'ficha-puesto', element: <FichaPuesto /> },
      // El cuestionario APARTE con el que se termina la visita a un mercado. Uno por
      // mercado y día: dos mercados el mismo día son dos cierres.
      { path: 'cierre-jornada', element: <CierreJornada /> },
      // Path EXACTO `solicitud` (NO `misolicitud`): lo consumen MOVIL_LINKS, la Task 27 y la Task 37.
      { path: 'solicitud', element: <MiSolicitud /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
